import { useEffect, useState } from "react";
import { Box, Button, Group, Text } from "@mantine/core";
import { useGo } from "../components/ConsoleShell";
import { Cursor, EmptyRow, EmptyState, MetaChips, Panel, Prompt, Screen, LoadError } from "../components/primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useUiStore } from "../stores/uiStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import css from "./screens.module.css";

export default function Documents() {
  const go = useGo();
  const openModal = useUiStore((s) => s.openModal);
  const openDetail = useUiStore((s) => s.openDetail);
  const workspaceId = useWorkspaceStore((s) => s.currentId);

  const namespaces = useConsoleStore((s) => s.namespaces);
  const loadError = useConsoleStore((s) => s.errors.documents);
  const documents = useConsoleStore((s) => s.documents);
  const lastUpsert = useConsoleStore((s) => s.lastUpsert);
  const loadNamespaces = useConsoleStore((s) => s.loadNamespaces);
  const loadDocuments = useConsoleStore((s) => s.loadDocuments);

  const [namespace, setNamespace] = useState<string | null>(null);

  useEffect(() => {
    if (workspaceId && namespaces.length === 0) void loadNamespaces();
  }, [workspaceId, namespaces.length, loadNamespaces]);

  useEffect(() => {
    if (!namespace && namespaces.length > 0) setNamespace(namespaces[0].id);
  }, [namespace, namespaces]);

  useEffect(() => {
    if (namespace) void loadDocuments(namespace);
  }, [namespace, loadDocuments]);

  if (namespaces.length === 0) {
    return (
      <Screen>
        <Prompt command="vf docs ls --namespace" comment="— no namespaces in this workspace" />
        <LoadError message={loadError} />
        <EmptyState
          title="create a namespace first"
          body="Documents live inside a namespace, which defines the embedder and dimensions used to index them."
          action={
            <Button size="md" fz="13.5px" fw={600} onClick={() => go("/namespaces/new")}>
              ▸ create a namespace
            </Button>
          }
        />
      </Screen>
    );
  }

  return (
    <Screen>
      <Prompt
        command="vf docs ls --namespace"
        action={
          <select
            className={`${css.nativeSelect} ${css.nativeSelectInline}`}
            value={namespace ?? ""}
            aria-label="namespace"
            onChange={(e) => setNamespace(e.currentTarget.value)}
          >
            {namespaces.map((n) => (
              <option key={n.id} value={n.id}>
                {n.label}
              </option>
            ))}
          </select>
        }
      />

      <Box
        p={16}
        mb={16}
        style={{ border: "1px solid var(--vf-border)", background: "var(--vf-panel)" }}
      >
        <Group justify="space-between" gap={16} wrap="nowrap">
          <Box>
            <Text fz="13px" c="var(--vf-text)" mb={4}>
              add a document
            </Text>
            <Text fz="11.5px" c="var(--vf-dim2)">
              content + metadata → async ingest via the durable pipeline
            </Text>
          </Box>
          <Button
            fz="13px"
            fw={600}
            onClick={() => openModal("upsertDoc")}
            style={{ whiteSpace: "nowrap" }}
          >
            + upsert document
          </Button>
        </Group>

        {lastUpsert && lastUpsert.namespace === namespace ? (
          <Box mt={11} pt={11} style={{ borderTop: "1px solid var(--vf-border)", fontSize: "12.5px" }}>
            <span style={{ color: "var(--vf-sec)" }}>202 accepted · </span>
            <span style={{ color: "var(--vf-acc)" }}>{lastUpsert.operationId}</span>
            <span style={{ color: "var(--vf-sec)" }}> · status </span>
            {lastUpsert.status === "committed" ? (
              <span style={{ color: "var(--vf-ok)" }}>✓ committed</span>
            ) : lastUpsert.status === "failed" ? (
              <span style={{ color: "var(--vf-bad)" }}>✗ failed</span>
            ) : (
              <span style={{ color: "var(--vf-warn)" }}>
                queued
                <Cursor />
              </span>
            )}
          </Box>
        ) : null}
      </Box>

      <Panel capsTitle title="documents · click to inspect">
        {documents.length === 0 ? (
          <EmptyRow
            title="this namespace is empty"
            body={
              <>
                Upsert a document above, or POST to{" "}
                <span style={{ color: "var(--vf-ok)" }}>
                  /v1/namespaces/{namespace}/documents
                </span>{" "}
                from your app.
              </>
            }
          />
        ) : (
          documents.map((d) => (
            <button
              key={d.id}
              type="button"
              className={css.docRow}
              onClick={() => openDetail("document", d.id)}
            >
              <Group gap={10} mb={6} align="center">
                <Text fz="13px" c="var(--vf-text)">
                  {d.id}
                </Text>
                {d.isNew ? <span className={css.newBadge}>NEW</span> : null}
              </Group>
              <p className={`${css.hitBody} ${css.hitBodyOne}`}>{d.content}</p>
              <MetaChips metadata={d.metadata} />
            </button>
          ))
        )}
      </Panel>
    </Screen>
  );
}
